import * as Dialog from '@radix-ui/react-dialog';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { useLayoutEffect, useRef } from 'react';
import { useIsDesktop } from '../../hooks/useMediaQuery';

interface ResponsiveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
}

/**
 * One overlay primitive for the whole app: a centred dialog at lg+, a bottom
 * sheet below it. Focus trap, focus restore, scroll lock and aria-modal come
 * from Radix rather than being hand-rolled per call site.
 *
 * EXCEPTION — focus-restore-to-trigger IS hand-rolled here, on purpose.
 * Radix's own restore is unreachable for a component shaped like this one:
 * `DialogContentModal` (node_modules/@radix-ui/react-dialog/dist/index.mjs,
 * ~lines 154-157) wires
 *   onCloseAutoFocus: composeEventHandlers(props.onCloseAutoFocus, (event) => {
 *     event.preventDefault();
 *     context.triggerRef.current?.focus();
 *   })
 * and `onCloseAutoFocus` IS `@radix-ui/react-focus-scope`'s
 * `onUnmountAutoFocus` (same file, ~line 226) — the event FocusScope uses to
 * decide whether to run its OWN generic "focus the previously-focused
 * element" fallback. Calling `event.preventDefault()` there unconditionally
 * cancels that fallback. `context.triggerRef` is populated only by a
 * `Dialog.Trigger` element, which this component never renders (it is fully
 * controlled via `open`/`onOpenChange`, with no built-in trigger) — so
 * Radix's handler always prevents default and then focuses `null`, which
 * *actively destroys* focus restore rather than merely skipping it. There is
 * no reachable path to Radix's built-in restore in modal mode for this
 * shape of component. This capture-and-restore was reviewed and explicitly
 * approved as a narrow, sanctioned exception (2026-07-30, fix round 2 of the
 * ResponsiveDialog task) — do not "clean this up" as redundant with Radix
 * without re-reading this comment and the citations above.
 */
export function ResponsiveDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
}: ResponsiveDialogProps) {
  const isDesktop = useIsDesktop();

  // Element to refocus once the dialog closes — whatever had focus at the
  // moment `open` became true (almost always the external trigger button).
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    // Depending on [open] means this only fires on the closed->open
    // transition (or initial mount with open=true), never on a re-render
    // while the dialog stays open — so it can't clobber the original opener
    // with something that gained focus inside the dialog later.
    //
    // useLayoutEffect (not useEffect) is required: passive effects run
    // bottom-up per commit, so `Dialog.Content`'s internal FocusScope — a
    // descendant of this component — would run its own mount-autofocus
    // *passive* effect (which moves focus into the dialog) before a plain
    // `useEffect` here ever ran, capturing the wrong element. Layout effects
    // all run, tree-wide, before any passive effect does, so this always
    // captures the pre-dialog focus target correctly.
    if (open) {
      restoreFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    }
  }, [open]);

  function handleCloseAutoFocus(event: Event) {
    // Cancel Radix's own handler (see the class-level comment) before it
    // runs, then restore focus ourselves.
    event.preventDefault();
    const target = restoreFocusRef.current;
    // The captured element may have unmounted while the dialog was open
    // (e.g. it lived in a list row that re-rendered away) — `.isConnected`
    // guards against calling `.focus()` on a detached node. If it's still
    // connected but no longer focusable (e.g. now disabled), `.focus()` is
    // a harmless no-op; we don't assert it succeeded.
    if (target && target.isConnected) {
      target.focus();
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50" />
        <Dialog.Content
          data-testid="responsive-dialog-content"
          data-layout={isDesktop ? 'dialog' : 'sheet'}
          onCloseAutoFocus={handleCloseAutoFocus}
          className={clsx(
            'fixed z-50 bg-surface-raised text-content shadow-2xl focus:outline-none',
            'flex flex-col',
            isDesktop
              ? 'left-1/2 top-1/2 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-lg max-h-[85vh]'
              : 'inset-x-0 bottom-0 max-h-[92vh] rounded-t-lg pb-safe-b',
          )}
        >
          <div className="px-5 py-4 border-b border-border-token flex items-start justify-between gap-4">
            <div>
              <Dialog.Title className="text-lg font-bold">{title}</Dialog.Title>
              {description ? (
                <Dialog.Description className="text-sm text-content-muted mt-1">
                  {description}
                </Dialog.Description>
              ) : null}
            </div>
            <Dialog.Close
              aria-label="Close"
              className="min-h-tap min-w-tap -mr-2 -mt-2 inline-flex items-center justify-center rounded-md text-content-muted hover:bg-surface-sunken"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Dialog.Close>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>

          {footer ? (
            <div className="px-5 py-4 border-t border-border-token flex justify-end gap-3">{footer}</div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
